/**
 * Planning-regels — planner-adres, capaciteitsregels en max draaidagen per persoon.
 */
import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Stack, Chip, Divider, Autocomplete } from '@mui/material';
import { SettingField, SectionHeader } from '../adminSettingsCommon';
import CapaciteitsRegels from '../../../ui-components/CapaciteitsRegels';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { leesJson, meldFout } from '../../../utils/apiFout';

function MaxDraaidagen() {
  const [rows, setRows] = useState(null);
  const [persons, setPersons] = useState([]);
  const [nieuwPersoon, setNieuwPersoon] = useState(null);
  const [nieuwMax, setNieuwMax] = useState(3);
  const [nieuwHeavy, setNieuwHeavy] = useState(2);

  const load = async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/draaidag-planner/config`);
      setRows((await leesJson(r, 'Max draaidagen laden')).config || []);
    } catch (e) { meldFout(e, 'Max draaidagen laden'); setRows([]); }
  };
  useEffect(() => {
    load();
    authFetch(`${BACKEND_URL}/Api/planner-todo/persons`).then(r => leesJson(r, 'Medewerkers laden'))
      .then(d => setPersons(d.persons || [])).catch((e) => meldFout(e, 'Medewerkers laden'));
  }, []);

  const save = async (name, role, max, maxHeavy) => {
    await authFetch(`${BACKEND_URL}/Api/draaidag-planner/config`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ person_name: name, role: role || undefined, max_per_week: Number(max), max_heavy_per_week: Number(maxHeavy) }),
    });
    load();
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Max draaidagen per week per persoon: <b>totaal</b> én <b>zwaar</b> (M/L/XL). Bv. cameraman: 3 totaal,
        2 zwaar (dus 3× S, of 2× S + 1× M, of 2× M/L). Zonder instelling: rol-default (camera 3/2, regie 2/2).
      </Typography>
      {rows === null ? <CircularProgress size={18} /> : (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} sx={{ px: 0.5 }}>
            <Box sx={{ flex: 1 }} /><Typography variant="caption" sx={{ width: 90, textAlign: 'center' }}>totaal/wk</Typography>
            <Typography variant="caption" sx={{ width: 90, textAlign: 'center' }}>zwaar/wk</Typography>
          </Stack>
          {rows.map((c) => (
            <Stack key={c.person_key} direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ flex: 1, fontSize: 14 }}>{c.person_name} {c.role && <Chip size="small" label={c.role} sx={{ height: 18, fontSize: 10 }} />}</Typography>
              <TextField type="number" size="small" defaultValue={c.max_per_week} sx={{ width: 90 }}
                inputProps={{ min: 0 }} onBlur={(e) => save(c.person_name, c.role, e.target.value, c.max_heavy_per_week ?? 2)} />
              <TextField type="number" size="small" defaultValue={c.max_heavy_per_week ?? 2} sx={{ width: 90 }}
                inputProps={{ min: 0 }} onBlur={(e) => save(c.person_name, c.role, c.max_per_week, e.target.value)} />
            </Stack>
          ))}
          {rows.length === 0 && <Typography variant="caption" color="text.secondary">Nog niemand ingesteld.</Typography>}
          <Divider sx={{ my: 1 }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Autocomplete size="small" sx={{ flex: 1 }} options={persons}
              getOptionLabel={(o) => o?.name || ''} value={nieuwPersoon}
              onChange={(_e, v) => setNieuwPersoon(v)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Medewerker" />} />
            <TextField type="number" size="small" label="Totaal/wk" value={nieuwMax} onChange={(e) => setNieuwMax(e.target.value)} sx={{ width: 95 }} inputProps={{ min: 0 }} />
            <TextField type="number" size="small" label="Zwaar/wk" value={nieuwHeavy} onChange={(e) => setNieuwHeavy(e.target.value)} sx={{ width: 95 }} inputProps={{ min: 0 }} />
            <Button variant="outlined" disabled={!nieuwPersoon}
              onClick={() => { if (nieuwPersoon) { save(nieuwPersoon.name, nieuwPersoon.function, nieuwMax, nieuwHeavy); setNieuwPersoon(null); } }}
              sx={{ textTransform: 'none', fontWeight: 700 }}>Toevoegen</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

export default function PlanningRegels() {
  return (
    <Box>
      <SectionHeader
        title="Mail-assistent (Frits)"
        subtitle="Instellingen voor de dagelijkse mail-triage en planning-concepten."
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
        <SettingField settingKey="assistant_planner_email" label="Planner e-mailadres"
          helperText="Waar concept-planningsmails naartoe gaan. Leeg = de 'planner'-knop staat uit." />
      </Box>
      <Box sx={{ mt: 2 }}>
        <SettingField settingKey="huisstijl_tone_of_voice" label="Full Frame tone-of-voice (bedrijfsbreed)" multiline rows={14}
          helperText="Basislaag voor alles wat AI namens Full Frame schrijft: Frits-concepten en begeleidende offerte-mails. Iedere medewerker heeft daarbovenop een eigen schrijfstijl-profiel (Frits-pagina, 🗣️-icoon) dat bij verschil vóórgaat. Leeg = geen basislaag." />
      </Box>
      <Box sx={{ mt: 2 }}>
        <SettingField settingKey="assistant_signature_html" label="E-mailhandtekening — bedrijfs-standaard (HTML)" multiline rows={6}
          helperText="Fallback-handtekening (HTML) voor iedereen die nog geen eigen handtekening heeft ingesteld. Iedere gebruiker stelt zijn eigen handtekening in op de Frits-pagina (✍️-icoon rechtsboven) — daar werkt gewoon copy-paste uit Outlook, mét opmaak en afbeeldingen." />
      </Box>

      <Box sx={{ mt: 4 }}>
        <SectionHeader title="Capaciteitsregels" subtitle="Regels voor het rooster, het Capaciteit-overzicht en Slimme toewijzing." />
      </Box>

      {/* Uitleg + instelbare regels; dezelfde component staat op de Capaciteit-pagina,
          zodat de planner er ook zonder admin-rechten bij kan. */}
      <CapaciteitsRegels />

      <Box sx={{ mt: 4 }}>
        <SectionHeader title="Uren-herinnering" subtitle="Dagelijkse push (werkdagen) naar wie vorige week te weinig uren schreef — met link naar Slim Uren. Herhaalt tot de uren geschreven zijn." />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
        <SettingField settingKey="uren_reminder_enabled" label="Aan/uit ('true'/'false')"
          helperText="Zet de dagelijkse uren-herinnering aan of uit." />
        <SettingField settingKey="uren_reminder_min_hours" label="Drempel (uren/week)"
          helperText="Minder dan dit aantal uren vorige week geschreven = herinnering." />
        <SettingField settingKey="uren_reminder_hour_utc" label="Tijdstip (UTC, HH:MM)"
          helperText="Dagelijks tijdstip in UTC. 06:45 UTC = 08:45 NL (zomertijd)." />
      </Box>

      <Box sx={{ mt: 4 }}>
        <SectionHeader title="Max draaidagen per week" subtitle="Per persoon — gebruikt door de draaidag-planner." />
      </Box>
      <MaxDraaidagen />

      <Box sx={{ mt: 4 }}>
        <SectionHeader title="Week-check draaidagen" subtitle="De regels- en agendacheck per week op de Draaidagen-pagina ('Nog ruimte deze week')." />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Telt alleen actieve <b>medewerkers</b> met functie camera of regie. Freelancers en stagiairs vallen er automatisch buiten.
        Staat iemand in Personen als medewerker maar werkt hij feitelijk als freelancer, zet zijn e-mailadres hieronder.
      </Typography>
      <SettingField settingKey="weekcheck_uitsluiten" label="Niet meetellen in de week-check (e-mailadressen, komma-gescheiden)"
        helperText="Bv. thijmen@fullframe.nl. Wijzigingen zijn na maximaal 10 minuten zichtbaar (cache)." />
    </Box>
  );
}
